import { StepPage } from 'containers/StepPage/StepPage';
import React, { useCallback, useMemo } from 'react';
import { noop } from 'lodash';
import { LoadingIndicator } from 'components/common/LoadingIndicator';
import { Redirect } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import i18n from 'i18n';
import { DynamicBreadcrumb } from 'components/Breadcrumbs/DynamicBreadcrumbs';
import { Page } from 'components/Page/Page';
import SummaryDetail from 'components/SummaryDetail/SummaryDetail';
import { PmpFormType } from 'core/pmp/Pmp';
import { PmpSetupFlowModel } from './PmpSetupFlowPageModel';
import { PmpForm } from './PmpForm';

export const PmpSetupFlowPage: React.FC<{
  channelOptions: SelectOptions[];
  channelSpaceOptionsMap: { [channelId: number]: SelectOptions[] };
  channelAgencyOptionsMap: { [channelId: number]: SelectOptions[] };
  useModel: (channelSpaceOptionsMap: {
    [channelId: number]: SelectOptions[];
  }) => PmpSetupFlowModel;
}> = ({
  channelOptions,
  channelSpaceOptionsMap,
  channelAgencyOptionsMap,
  useModel
}) => {
  const {
    type,
    pmp,
    title,
    loading,
    redirectPath,
    summaryData,
    readonly,
    haveBoundCampaign,
    setPmp,
    backToList,
    handleSubmit
  } = useModel(channelSpaceOptionsMap);

  const renderForm = useCallback(
    ({ goNext }) => {
      const onSubmit = (pmp: PmpFormType) => {
        setPmp(pmp);
        goNext();
      };

      if (type === 'summary') {
        return <div />;
      }

      return (
        <PmpForm
          type={type}
          haveBoundCampaign={haveBoundCampaign}
          pmpFormData={pmp!}
          onSubmit={onSubmit}
          channelOptions={channelOptions}
          channelSpaceOptionsMap={channelSpaceOptionsMap}
          channelAgencyOptionsMap={channelAgencyOptionsMap}
        />
      );
    },
    [
      type,
      pmp,
      haveBoundCampaign,
      channelOptions,
      channelSpaceOptionsMap,
      channelAgencyOptionsMap,
      setPmp
    ]
  );

  const renderSummary = useCallback(() => {
    return (
      <Page.Section title={i18n.t('pmpSetupFlowPage.titles.summarySection')}>
        <SummaryDetail data={summaryData} />
      </Page.Section>
    );
  }, [summaryData]);

  const renderSummparyBtns = useCallback(
    ({ goLast }) => {
      if (!pmp) {
        return <div />;
      }

      if (readonly) {
        return (
          <Button variant='primary' size='sm' onClick={backToList}>
            {i18n.t('common.buttons.ok')}
          </Button>
        );
      }

      const onSubmit = () => {
        handleSubmit(pmp);
      };
      return (
        <>
          <Button size='sm' onClick={onSubmit}>
            {i18n.t('common.buttons.submit')}
          </Button>
          <Button variant='secondary' size='sm' onClick={goLast}>
            {i18n.t('common.buttons.back')}
          </Button>
        </>
      );
    },
    [pmp, readonly, handleSubmit, backToList]
  );

  const breadcrumbRoutes = useMemo(
    () => [
      {
        path: '/pmp',
        breadcrumb: i18n.t('appMenus.comapnyManagement.items.pmp')
      },
      {
        path: '/pmp/new',
        breadcrumb: i18n.t('pmpSetupFlowPage.titles.create')
      },
      {
        path: '/pmp/:pmpId',
        breadcrumb: DynamicBreadcrumb,
        props: { label: pmp ? pmp.name : '', matchParam: 'pmpId' }
      }
    ],
    [pmp]
  );

  if (!pmp) {
    return <LoadingIndicator />;
  }

  return (
    <>
      {loading && <LoadingIndicator />}
      {redirectPath && <Redirect to={redirectPath} />}
      <StepPage
        cancel={noop}
        withSidebar={false}
        title={title}
        spaceBetweenTopAreaAndContent={false}
        breadcrumbsRoutes={breadcrumbRoutes}
      >
        {!readonly && <StepPage.Step label='' renderStepContent={renderForm} />}
        <StepPage.Step
          label=''
          renderStepContent={renderSummary}
          renderBtns={renderSummparyBtns}
        />
      </StepPage>
    </>
  );
};
